// 허들 — 서버의 자리(맥박)와 SFU 의 소리(`Line`)를 한 줄로 묶는다.
//
// ## 순서가 곧 정확성이다
// 들어가기는 **서버 먼저, 소리 나중**이다. 서버가 자리를 주지 않으면(가득 참·안 든 채널)
// 마이크를 켤 이유가 없다. 소리가 안 붙으면(마이크 거부) 방금 받은 자리를 곧바로 돌려준다 —
// 안 그러면 소리 없는 사람이 45초 동안 남의 명단에 떠 있다.
//
// ## 늦게 끝난 약속은 버린다
// 붙는 중에 나가기를 누르거나 다른 채널로 옮기면, 앞선 약속이 나중에 끝나도 아무것도
// 바꾸지 않는다(`#epoch`). 버린 약속이 이미 소리를 붙였으면 그 자리에서 끊는다.
//
// ## 맥박이 자리다
// 서버는 `HUDDLE_BEAT_SECS` 의 세 배 동안 소식이 없으면 뺀다. 그래서 있는 동안 같은 간격으로
// 들어가기를 다시 부른다. 404 면 그 사이 채널에서 빠진 것이라 나온다.

import { ApiError, type HuddleTicket, type MaruApi, type Session } from '@maru/client'
import { type ChannelId, type Event, HUDDLE_BEAT_SECS, type UserId } from '@maru/protocol'
import {
  type HuddleHands,
  type HuddleMine,
  type HuddleTile,
  locale_now,
  msg,
  translate,
  trouble,
} from '@maru/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import type { CaptionLine } from './captions.ts'

/** 붙은 소리 한 줄. */
export interface Line {
  mute(on: boolean): Promise<void>
  /** 자막을 켜고 끈다. 참이면 이 기기가 내 말도 받아 적는다(기기 안 인식이 된다). */
  captions(on: boolean): Promise<boolean>
  /** 카메라를 켜고 끈다. 권한이 없으면 던진다. */
  camera(on: boolean): Promise<void>
  /** 화면 공유. 사람이 고르기 창을 닫으면 거짓(실패가 아니다), 그 밖의 실패는 던진다. */
  screen(on: boolean): Promise<boolean>
  close(): void
}

/** 소리 줄이 알리는 영상 상태. 브라우저의 "공유 중지" 막대로 꺼진 것도 여기로 온다. */
export interface Media {
  tiles: readonly HuddleTile[]
  camera: boolean
  screen: boolean
}

/**
 * 입장권으로 소리를 붙인다. SFU 가 끊으면 `dropped` 를, 보일 자막 줄이 바뀌면 `shown` 을
 * 부른다.
 */
export type Dial = (
  ticket: HuddleTicket,
  dropped: () => void,
  shown: (lines: readonly CaptionLine[]) => void,
  media?: (m: Media) => void,
) => Promise<Line>

export interface HuddleApi {
  huddle_join(channel: ChannelId): Promise<{ ticket: HuddleTicket; people: UserId[] }>
  huddle_leave(channel: ChannelId): Promise<void>
}

export interface Timers {
  every(ms: number, run: () => void): () => void
}

const real_timers: Timers = {
  every: (ms, run) => {
    const id = setInterval(run, ms)
    return () => clearInterval(id)
  },
}

/** 서버의 거절을 사람 말로. 404 는 허들에서 늘 "구성원이 아니다" 는 뜻이다. */
export function say_refusal(e: unknown): string {
  if (e instanceof ApiError && e.status === 404)
    return translate(locale_now(), msg('이 대화의 구성원이어야 허들에 참여할 수 있어요.'))
  if (e instanceof ApiError) return trouble(e)
  return translate(locale_now(), msg('허들에 참여하지 못했어요. 잠시 후 다시 시도해 주세요.'))
}

/** 영상 상태를 자리에 얹는다. 꺼진 것은 칸째 뺀다 — 소리만 있는 허들은 모양이 그대로다. */
export function with_media(mine: HuddleMine, m: Media): HuddleMine {
  const { camera: _c, screen: _s, tiles: _t, ...rest } = mine
  return {
    ...rest,
    ...(m.camera ? { camera: true } : {}),
    ...(m.screen ? { screen: true } : {}),
    ...(m.tiles.length > 0 ? { tiles: m.tiles } : {}),
  }
}

/** 이 기기가 화면을 공유할 수 있는가(휴대폰 브라우저는 대개 못 한다). */
export function can_share_screen(nav: unknown = globalThis.navigator): boolean {
  const devices = (nav as { mediaDevices?: { getDisplayMedia?: unknown } } | undefined)
    ?.mediaDevices
  return typeof devices?.getDisplayMedia === 'function'
}

export class HuddleCall {
  readonly #api: HuddleApi
  readonly #dial: Dial
  readonly #changed: (mine: HuddleMine | null, error: string | null) => void
  readonly #timers: Timers
  #mine: HuddleMine | null = null
  #error: string | null = null
  #line: Line | null = null
  #stop_beat: (() => void) | null = null
  #epoch = 0
  #media: Media = { tiles: [], camera: false, screen: false }

  constructor(
    api: HuddleApi,
    dial: Dial,
    changed: (mine: HuddleMine | null, error: string | null) => void,
    timers: Timers = real_timers,
  ) {
    this.#api = api
    this.#dial = dial
    this.#changed = changed
    this.#timers = timers
  }

  get mine(): HuddleMine | null {
    return this.#mine
  }

  async join(channel: ChannelId): Promise<void> {
    if (this.#mine?.channel === channel) return
    // 앞 방의 소리만 끊는다. 서버 자리는 들어가기가 스스로 옮긴다.
    this.#hang_up()
    const epoch = ++this.#epoch
    this.#media = { tiles: [], camera: false, screen: false }
    this.#set({ channel, muted: false, connecting: true }, null)
    let ticket: HuddleTicket
    try {
      ticket = (await this.#api.huddle_join(channel)).ticket
    } catch (e) {
      if (epoch === this.#epoch) this.#set(null, say_refusal(e))
      return
    }
    if (epoch !== this.#epoch) return
    let line: Line
    try {
      line = await this.#dial(
        ticket,
        () => {
          if (epoch === this.#epoch) void this.leave()
        },
        (lines) => {
          const now = this.#mine
          if (epoch !== this.#epoch || now?.captions === undefined) return
          this.#set({ ...now, captions: { ...now.captions, lines } }, this.#error)
        },
        (m) => {
          if (epoch !== this.#epoch) return
          this.#media = m
          const now = this.#mine
          if (now !== null) this.#set(with_media(now, m), this.#error)
        },
      )
    } catch {
      if (epoch !== this.#epoch) return
      this.#set(
        null,
        translate(
          locale_now(),
          msg(
            '마이크를 쓸 수 없어서 허들에 참여하지 못했어요. 브라우저에서 마이크 권한을 허용해 주세요.',
          ),
        ),
      )
      this.#api.huddle_leave(channel).catch(() => {})
      return
    }
    if (epoch !== this.#epoch) {
      line.close()
      return
    }
    this.#line = line
    this.#stop_beat = this.#timers.every(HUDDLE_BEAT_SECS * 1000, () => this.#beat(channel, epoch))
    this.#set(with_media({ channel, muted: false, connecting: false }, this.#media), null)
  }

  /** 나온다. 서버가 못 받아도 이 기기의 소리는 먼저 끊는다 — 자리는 맥박이 끊기면 빠진다. */
  async leave(): Promise<void> {
    const was = this.#mine
    this.#epoch++
    this.#hang_up()
    this.#set(null, this.#error)
    if (was !== null) await this.#api.huddle_leave(was.channel).catch(() => {})
  }

  async mute(on: boolean): Promise<void> {
    const line = this.#line
    const mine = this.#mine
    if (line === null || mine === null) return
    await line.mute(on)
    if (this.#mine === mine) this.#set({ ...mine, muted: on }, this.#error)
  }

  /** 자막. 끄면 줄을 지운다 — 남지 않는 것이 규칙이다(`captions.ts`). */
  async captions(on: boolean): Promise<void> {
    const line = this.#line
    const mine = this.#mine
    if (line === null || mine === null) return
    const self = await line.captions(on)
    const now = this.#mine
    if (now === null || now.channel !== mine.channel) return
    const { captions: _, ...rest } = now
    this.#set(
      on ? { ...rest, captions: { lines: now.captions?.lines ?? [], self } } : rest,
      this.#error,
    )
  }

  /** 카메라. 권한이 없으면 사람 말로 알리고 꺼 둔다. */
  async camera(on: boolean): Promise<void> {
    const line = this.#line
    const mine = this.#mine
    if (line === null || mine === null) return
    try {
      await line.camera(on)
    } catch {
      if (this.#mine?.channel === mine.channel)
        this.#set(
          this.#mine,
          translate(
            locale_now(),
            msg('카메라를 쓸 수 없어요. 브라우저에서 카메라 권한을 허용해 주세요.'),
          ),
        )
      return
    }
    this.#media = { ...this.#media, camera: on }
    const now = this.#mine
    if (now?.channel === mine.channel) this.#set(with_media(now, this.#media), this.#error)
  }

  /** 화면 공유. 고르기 창을 닫은 것은 실패가 아니다 — 아무 말 없이 꺼 둔다. */
  async screen(on: boolean): Promise<void> {
    const line = this.#line
    const mine = this.#mine
    if (line === null || mine === null) return
    let sharing: boolean
    try {
      sharing = (await line.screen(on)) && on
    } catch {
      if (this.#mine?.channel === mine.channel)
        this.#set(
          this.#mine,
          translate(locale_now(), msg('화면을 공유하지 못했어요. 잠시 후 다시 시도해 주세요.')),
        )
      return
    }
    this.#media = { ...this.#media, screen: sharing }
    const now = this.#mine
    if (now?.channel === mine.channel) this.#set(with_media(now, this.#media), this.#error)
  }

  clear_error(): void {
    this.#set(this.#mine, null)
  }

  #beat(channel: ChannelId, epoch: number) {
    this.#api.huddle_join(channel).catch((e: unknown) => {
      // 채널에서 빠졌다. 그 밖의 실패(망 흔들림)는 다음 맥박에 맡긴다.
      if (epoch === this.#epoch && e instanceof ApiError && e.status === 404) void this.leave()
    })
  }

  #hang_up() {
    this.#stop_beat?.()
    this.#stop_beat = null
    this.#line?.close()
    this.#line = null
  }

  #set(mine: HuddleMine | null, error: string | null) {
    this.#mine = mine
    this.#error = error
    this.#changed(mine, error)
  }
}

/**
 * 화면에 넘길 허들 손. 서버에서 꺼져 있으면 `undefined` — 🎧 단추도 도크도 없다.
 *
 * 명단은 `GET /v1/huddles` 하나다. `huddle` 사건이 오면 다시 받고, 맥박이 끊겨 조용히
 * 빠진 사람은 사건이 없으니 30초마다도 다시 받는다.
 */
export function useHuddleHands(api: MaruApi, session: Session): HuddleHands | undefined {
  const queries = useQueryClient()
  const status = useQuery({
    queryKey: ['huddles'],
    queryFn: () => api.huddles(),
    refetchInterval: 30_000,
  })
  const [state, set_state] = useState<{ mine: HuddleMine | null; error: string | null }>({
    mine: null,
    error: null,
  })
  const [call] = useState(
    () =>
      new HuddleCall(
        api,
        // 소리·자막 코드는 처음 들어갈 때 받는다 — 첫 화면에 싣지 않는다(livekit-client 와 같은 이유).
        async (...a) => (await import('./huddle_line.ts')).dial_livekit(...a),
        (mine, error) => set_state({ mine, error }),
      ),
  )
  useEffect(
    () =>
      session.on((e: Event) => {
        if (e.t === 'huddle') void queries.invalidateQueries({ queryKey: ['huddles'] })
      }),
    [session, queries],
  )
  // 로그아웃·창 닫기에서도 소리를 끊고 자리를 돌려준다. 창을 닫을 때의 요청은 못 닿을
  // 수 있다 — 그래도 맥박이 멈추니 45초 뒤에는 빠진다.
  useEffect(() => {
    const bye = () => void call.leave()
    globalThis.addEventListener?.('pagehide', bye)
    return () => {
      globalThis.removeEventListener?.('pagehide', bye)
      bye()
    }
  }, [call])
  if (status.data?.enabled !== true) return undefined
  return {
    live: new Map(status.data.live.map((l) => [l.channel, l.people])),
    mine: state.mine,
    error: state.error,
    join: (channel) => void call.join(channel),
    leave: () => void call.leave(),
    mute: (on) => void call.mute(on),
    captions: (on) => void call.captions(on),
    camera: (on) => void call.camera(on),
    ...(can_share_screen() ? { screen: (on: boolean) => void call.screen(on) } : {}),
    clear_error: () => call.clear_error(),
  }
}
